import { useState, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { UserIcon } from 'lucide-react'
import { Combobox, ComboboxOption } from '@/components/ui/combobox'
import { useUsers } from '@/hooks'

type UserComboboxProps = {
  id?: string
  value?: string | string[]
  onValueChange?: (value: string | string[]) => void
  multiple?: boolean
  placeholder?: string
  disabled?: boolean
  className?: string
  clearable?: boolean
  error?: boolean
}

export function UserCombobox({
  id,
  value,
  onValueChange,
  multiple,
  placeholder,
  disabled = false,
  className,
  clearable = true,
  error: hasError = false
}: UserComboboxProps) {
  const t = useTranslations()
  const [search, setSearch] = useState('')

  const { data, isLoading } = useUsers({ q: search || undefined, size: 20, page: 1 })

  const options: ComboboxOption[] = useMemo(() => {
    if (!data?.items) {
      return []
    }
    return data.items.map(user => ({
      label: user.fullName,
      value: user.id,
      searchValue: `${user.fullName} ${user.email}`,
      description: user.email
    }))
  }, [data])

  return (
    <Combobox
      id={id}
      options={options}
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder || t('select_user')}
      searchPlaceholder={t('search_user')}
      emptyText={isLoading ? t('loading') : t('not_found')}
      disabled={disabled || isLoading}
      icon={<UserIcon className="h-4 w-4" />}
      clearable={clearable}
      error={hasError}
      className={className}
      multiple={!!multiple}
      onSearchChange={setSearch}
    />
  )
}
